import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { getPageTitle, getMetaDescription } from "../utils/pageTitle";
import {
  Box,
  Container,
  HStack,
  VStack,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  Button,
  SimpleGrid,
  Heading,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Icon,
} from "@chakra-ui/react";
import { SearchIcon, ChevronDownIcon } from "@chakra-ui/icons";
import { FaFileAlt } from "react-icons/fa";
import BlogCard from "../components/BlogCard";
import { motion } from "framer-motion";

const MotionBox = motion(Box);
const MotionVStack = motion(VStack);
const MotionHStack = motion(HStack);
const MotionSimpleGrid = motion(SimpleGrid);
const MotionInputGroup = motion(InputGroup);

// Dynamically import all blog posts from the posts directory
function importAllPosts() {
  const postContext = require.context('./blogs/posts', false, /\.js$/);
  const posts = [];
  
  postContext.keys().forEach(key => {
    const post = postContext(key).post;
    if (post) {
      posts.push(post);
    }
  });
  
  // Sort posts by date (newest first)
  return posts.sort((a, b) => {
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    return dateB - dateA;
  });
}

// Get all blog posts
const blogPosts = importAllPosts();

const BlogPageComponent = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");

  // Extract all unique categories from blog posts
  const allCategories = ["all", ...new Set(
    blogPosts.flatMap(post => 
      Array.isArray(post.category) ? post.category : [post.category]
    )
  )];

  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === "all" || 
                           (Array.isArray(post.category) 
                             ? post.category.includes(categoryFilter) 
                             : post.category === categoryFilter);
    return matchesSearch && matchesCategory;
  });

  const getCategoryDisplayName = (value) => {
    return value === "all" ? "All Categories" : value;
  };

  return (
    <Box 
      pt={{ base: 32, md: 32 }}
      pb={{ base: 20, md: 24 }}
      px={4} 
      bg="brand.primary" 
      minH="100vh"
      position="relative"
      overflow="hidden"
    >      <Helmet>
        <title>{getPageTitle("Blog")}</title>
        <meta name="description" content={getMetaDescription("Explore our latest insights and reflections on various topics.")} />
      </Helmet>
      
      {/* Background decoration elements - similar to homepage */}
      <Box
        position="absolute"
        top="5%"
        left="20%"
        w="300px"
        h="300px"
        borderRadius="full"
        bg="brand.bg.hover"
        filter="blur(80px)"
        opacity="0.1"
        zIndex="0"
      />
      <Box
        position="absolute"
        bottom="10%"
        right="5%"
        w="250px"
        h="250px"
        borderRadius="full"
        bg="rgba(79, 209, 197, 0.2)"
        filter="blur(100px)"
        opacity="0.15"
        zIndex="0"
      />
      
      {/* Additional color splashes */}
      <Box
        position="absolute"
        top="25%"
        right="15%"
        w="180px"
        h="180px"
        borderRadius="full"
        bgGradient="linear(to-r, purple.400, blue.300)"
        filter="blur(90px)"
        opacity="0.07"
        zIndex="0"
      />
      <Box
        position="absolute"
        bottom="30%"
        left="10%"
        w="220px"
        h="220px"
        borderRadius="full"
        bgGradient="linear(to-br, pink.300, red.200)"
        filter="blur(100px)"
        opacity="0.06"
        zIndex="0"
      />
      
      <Container maxW="container.xl" position="relative" zIndex="1">
        <MotionVStack 
          spacing={12} 
          align="stretch"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <MotionBox
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            alignSelf="center"
          >
            <Text 
              id="blog-section"
              fontSize={{ base: "3xl", md: "4xl" }}
              fontWeight="bold"
              color="white"
              textAlign="center"
              position="relative"
              display="inline-block"
              pb={4}
              _after={{
                content: '""',
                position: "absolute",
                bottom: 0,
                left: "50%",
                transform: "translateX(-50%)",
                width: { base: "60px", md: "80px" },
                height: "4px",
                bg: "white",
                borderRadius: "2px"
              }}
            >
              Insights & Reflections
            </Text>
          </MotionBox>

          <MotionHStack 
            width="100%" 
            spacing={1} 
            alignItems="center"
            flexWrap="nowrap"
            justifyContent={{ base: "flex-start", md: "center" }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <MotionInputGroup maxWidth={{ base: "none", md: "400px" }}> 
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="Search blog posts..."
                bg="rgba(255, 255, 255, 0.35)"
                color="white"
                borderRadius="md"
                borderColor="rgba(255, 255, 255, 0.2)"
                _placeholder={{ color: 'gray.400' }}
                _hover={{ bg: "rgba(255, 255, 255, 0.4)" }}
                _focus={{ bg: "rgba(255, 255, 255, 0.4)", borderColor: "blue.500", boxShadow: "none" }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </MotionInputGroup>
            
            <Menu>
              <MenuButton
                as={Button}
                bg="rgba(255, 255, 255, 0.35)"
                color="white"
                borderRadius="md"
                borderWidth="1px"
                borderColor="rgba(255, 255, 255, 0.2)"
                _hover={{ bg: "rgba(255, 255, 255, 0.4)" }}
                _active={{ bg: "rgba(255, 255, 255, 0.45)" }}
                minWidth={{ base: "140px", md: "200px" }} 
                textAlign="left"
                fontWeight="normal"
                rightIcon={<ChevronDownIcon />}
                flexShrink={0}
              >
                {getCategoryDisplayName(categoryFilter)}
              </MenuButton>
              <MenuList 
                bg="rgba(45, 55, 72, 0.95)"
                borderColor="rgba(74, 85, 104, 0.9)"
                backdropFilter="blur(5px)"
                minWidth="200px"
              >
                {allCategories.map((category) => (
                  <MenuItem 
                    key={category} 
                    onClick={() => setCategoryFilter(category)}
                    bg={category === categoryFilter ? "rgba(66, 153, 225, 0.2)" : "transparent"}
                    color="white"
                    _hover={{md: { bg: "rgba(66, 153, 225, 0.8)" } }}
                    _focus={{md: { bg: "rgba(49, 130, 206, 0.8)", boxShadow: "outline" }}}
                  >
                    {getCategoryDisplayName(category)}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          </MotionHStack>

          <MotionSimpleGrid
            columns={{ base: 1, md: 2, lg: 3 }}
            spacing={10}
            w="full"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            {filteredPosts.map((post, index) => (
              <BlogCard
                key={post.id}
                {...post}
                index={index}
                useAnimation={true}
              />
            ))}
          </MotionSimpleGrid>
          
          {/* More Coming Soon Section */}
          <MotionBox
            mt={10}
            p={8}
            borderRadius="xl"
            bg="rgba(45, 55, 72, 0.6)"
            backdropFilter="blur(8px)"
            boxShadow="0 4px 20px rgba(0, 0, 0, 0.1)"
            textAlign="center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <Box mb={5}>
              <Icon as={FaFileAlt} fontSize="4xl" color="brand.accent" />
            </Box>
            <Heading size="lg" mb={3} fontWeight="600">
              More Content Coming Soon
            </Heading>
            <Text fontSize="md" color="gray.300" maxW="600px" mx="auto">
              I'm currently working on new, insightful articles. Stay tuned!
            </Text>
          </MotionBox>
        </MotionVStack>
      </Container>
    </Box>
  );
};

export default BlogPageComponent;